import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import {
  DIM_DEFS,
  bucketDetail,
  bucketLabel,
  describe,
  drill,
  loadFacet,
  type Bucket,
  type DimId,
} from "../../state/explore";
import { fetchPieces } from "../../data/api";
import { cities, composers, orgs, selectedComposerId, setPieceFilter } from "../../state/store";
import { countryDisplayName } from "../../utils/format";
import type { Piece } from "../../types";

/** Bottom sheet on phones, centred dialog on wide screens. Escape closes; body scroll is
 *  locked while open; focus lands on the first focusable child so a keyboard/VoiceOver
 *  reader isn't left behind on the page underneath. */
export function ExploreSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ComponentChildren }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const first = ref.current?.querySelector<HTMLElement>("input") ?? ref.current?.querySelector<HTMLElement>("button");
    first?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div class="xp-sheet-backdrop" onClick={onClose}>
      <div
        class="xp-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="xp-sheet-grip" aria-hidden="true" />
        <header class="xp-sheet-head">
          <h3 class="xp-sheet-title">{title}</h3>
          <button type="button" class="xp-sheet-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div class="xp-sheet-body">{children}</div>
      </div>
    </div>
  );
}

const PAGE = 200;
/** The API's keys= cap per call. */
const MAX_KEYS = 50;

function fold(s: string): string {
  return s.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
}

/** Every entity of an id-keyed dimension the browser already holds (composers.json,
 *  orgs.json, cities.json — the whole catalogue, not the slice), with the text a query
 *  is matched against and the all-time count used to rank candidates before the API is
 *  asked which of them are in the slice. */
function catalogueOf(dim: DimId): Array<{ key: string; text: string; count: number }> {
  switch (dim) {
    case "composer":
      return composers.value.map((c) => ({ key: String(c.id), text: fold(`${c.name} ${c.sort_name}`), count: c.count }));
    case "org":
      return orgs.value.map((o) => ({ key: String(o.id), text: fold(o.name), count: o.count }));
    case "city":
      return cities.value.map((c) => ({
        key: String(c.id),
        text: fold(`${c.name} ${countryDisplayName(c.country_code, c.country_name)}`),
        count: c.count,
      }));
    default:
      return [];
  }
}

/** The full, searchable list behind a panel's "All…" — pages through the facet 200 rows
 *  at a time (the API caps at 200 per call, offset ≤ 1000) and filters what's loaded
 *  locally. A query must NOT stop at the loaded head, though: a composer ranked 300th in
 *  a slice of thousands is exactly who a reader types a name for. So composers, orgs and
 *  cities are also matched against the whole in-browser catalogue, and the API is asked
 *  for the slice counts of those keys (≤50 per query, most-programmed first) — the hits
 *  land in the same list with the same slice-scoped numbers. Pieces (no local catalogue)
 *  search server-side instead; those hits carry all-time counts and say so. */
export function FacetSheet({
  dim,
  title,
  exclude,
  own = null,
  onClose,
  onDrill,
}: {
  dim: DimId;
  title: string;
  exclude?: string[];
  own?: "only" | "others" | null;
  onClose: () => void;
  onDrill: () => void;
}) {
  const def = DIM_DEFS[dim];
  const [query, setQuery] = useState("");
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [matched, setMatched] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [catalogue, setCatalogue] = useState<Piece[]>([]);
  // Slice counts for catalogue matches beyond the loaded head (composer / org / city).
  const [deep, setDeep] = useState<{ needle: string; buckets: Bucket[]; capped: boolean } | null>(null);
  const [deepLoading, setDeepLoading] = useState(false);
  const offsetRef = useRef(0);
  // The descriptor is read at open time; the sheet closes on any drill, so it never has
  // to track live filter changes.
  const baseRef = useRef(describe(dim, { limit: PAGE, own }));

  async function loadPage() {
    setLoading(true);
    try {
      const res = await loadFacet({ ...baseRef.current, offset: offsetRef.current });
      // Both-era pages are ranked from row 0 each time (see runMerged), so a tie at the page
      // boundary can land on either side — never list a key twice.
      setBuckets((prev) => {
        if (offsetRef.current === 0) return res.buckets;
        const have = new Set(prev.map((b) => b.key));
        return [...prev, ...res.buckets.filter((b) => !have.has(b.key))];
      });
      setMatched(res.matched);
      offsetRef.current += PAGE;
      setHasMore(res.truncated && offsetRef.current <= 1000);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPage();
  }, []);

  // Server-side catalogue search for pieces (debounced), scoped to the composer filter.
  useEffect(() => {
    if (dim !== "piece" || query.trim().length < 2) {
      setCatalogue([]);
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(() => {
      fetchPieces(query, { composer: selectedComposerId.peek(), limit: 12, signal: controller.signal })
        .then(setCatalogue)
        .catch(() => {});
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [dim, query]);

  const needle = fold(query.trim());
  const catalogueAll = useMemo(() => catalogueOf(dim), [dim, composers.value, orgs.value, cities.value]);
  const hasCatalogue = catalogueAll.length > 0;

  // Whole-catalogue lookup for id-keyed dims (debounced): candidates the head hasn't
  // loaded, ranked by all-time count, then their counts in THIS slice via keys=.
  useEffect(() => {
    if (!hasCatalogue || needle.length < 2) {
      setDeep(null);
      setDeepLoading(false);
      return;
    }
    const loaded = new Set(buckets.map((b) => b.key));
    const candidates = catalogueAll
      .filter((c) => !loaded.has(c.key) && !exclude?.includes(c.key) && c.text.includes(needle))
      .sort((a, b) => b.count - a.count);
    const keys = candidates.slice(0, MAX_KEYS).map((c) => c.key);
    if (!keys.length) {
      setDeep({ needle, buckets: [], capped: false });
      setDeepLoading(false);
      return;
    }
    const controller = new AbortController();
    setDeepLoading(true);
    const handle = setTimeout(() => {
      loadFacet({ ...baseRef.current, offset: 0, limit: MAX_KEYS, keys }, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          setDeep({ needle, buckets: res.buckets.filter((b) => b.n > 0), capped: candidates.length > MAX_KEYS });
        })
        .catch(() => {})
        .finally(() => {
          if (!controller.signal.aborted) setDeepLoading(false);
        });
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [needle, buckets, catalogueAll, hasCatalogue]);

  const headMatches = buckets
    .filter((b) => !exclude?.includes(b.key))
    .filter((b) => !needle || fold(bucketLabel(dim, b)).includes(needle) || fold(bucketDetail(dim, b) ?? "").includes(needle));
  // Deep hits are current only for the needle they were fetched for (a stale set would
  // flash the previous query's names under the new one).
  const deepHits = deep && deep.needle === needle ? deep.buckets : [];
  const filtered = deepHits.length
    ? [...headMatches, ...deepHits.filter((d) => !headMatches.some((h) => h.key === d.key))].sort((a, b) => b.n - a.n)
    : headMatches;
  const loadedKeys = new Set(filtered.map((b) => b.key));
  const catalogueExtra = catalogue.filter((p) => !loadedKeys.has(p.key));
  const maxN = filtered.reduce((m, b) => Math.max(m, b.n), 1);
  const deepPending = hasCatalogue && needle.length >= 2 && (deepLoading || (deep !== null && deep.needle !== needle));

  return (
    <ExploreSheet title={title} onClose={onClose}>
      <input
        class="xp-sheet-search"
        type="search"
        placeholder={`Search ${def.title.toLowerCase()}…`}
        value={query}
        onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
        autocomplete="off"
        enterkeyhint="search"
      />
      <p class="xp-note">
        {needle
          ? hasCatalogue
            ? needle.length < 2
              ? `${filtered.length.toLocaleString()} of ${buckets.length.toLocaleString()} loaded match · type more to search all ${catalogueAll.length.toLocaleString()}`
              : deepPending
                ? `${filtered.length.toLocaleString()} match so far · searching all ${catalogueAll.length.toLocaleString()} ${def.title.toLowerCase()}…`
                : `${filtered.length.toLocaleString()} match in this slice · searched all ${catalogueAll.length.toLocaleString()} ${def.title.toLowerCase()}${deep?.capped ? ` (${MAX_KEYS} most-programmed checked, narrow the search for more)` : ""}`
            : `${filtered.length.toLocaleString()} of ${buckets.length.toLocaleString()} loaded match`
          : `${buckets.length.toLocaleString()} loaded${hasMore ? " (more below)" : ""}`}
        {" · shares of "}
        {matched.toLocaleString()} concerts
      </p>
      <ol class="xp-rows xp-rows-sheet">
        {filtered.map((b, i) => {
          const label = bucketLabel(dim, b);
          const detail = bucketDetail(dim, b);
          const share = matched > 0 ? (b.n / matched) * 100 : 0;
          return (
            <li key={b.key}>
              <button
                type="button"
                class={`xp-row${dim === "piece" ? " xp-row-wrap" : ""}`}
                onClick={() => {
                  if (drill(dim, b)) onDrill();
                }}
              >
                <span class="xp-rank">{i + 1}</span>
                <span class="xp-row-main">
                  <span class="xp-row-name">
                    {label}
                    {detail && <span class="xp-row-detail">{detail}</span>}
                  </span>
                  <span class="xp-bar-track">
                    <span class="xp-bar" style={{ width: `${Math.max(1.5, (b.n / maxN) * 100)}%` }} />
                  </span>
                </span>
                <span class="xp-row-num">
                  <span class="xp-row-n">{b.n.toLocaleString()}</span>
                  <span class="xp-row-sub">{share >= 1 ? `${Math.round(share)}%` : ""}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {!loading && !deepPending && filtered.length === 0 && !catalogueExtra.length && (
        <p class="xp-note">
          {!needle ? "Nothing in this slice." : hasCatalogue && needle.length >= 2 ? `No ${def.title.toLowerCase()} matching "${query}" in this slice.` : `Nothing loaded matches "${query}".`}
        </p>
      )}
      {catalogueExtra.length > 0 && (
        <>
          <p class="xp-note xp-note-strong">Catalogue matches (all-time counts, not this slice)</p>
          <ol class="xp-rows xp-rows-sheet">
            {catalogueExtra.map((p) => (
              <li key={p.key}>
                <button
                  type="button"
                  class="xp-row xp-row-wrap"
                  onClick={() => {
                    setPieceFilter(p);
                    onDrill();
                  }}
                >
                  <span class="xp-rank" />
                  <span class="xp-row-main">
                    <span class="xp-row-name">
                      {p.label}
                      {p.composer && <span class="xp-row-detail">{p.composer}</span>}
                    </span>
                  </span>
                  <span class="xp-row-num">
                    <span class="xp-row-n">{p.count.toLocaleString()}</span>
                    <span class="xp-row-sub">all-time</span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </>
      )}
      {hasMore && !needle && (
        <button type="button" class="xp-link-btn xp-sheet-more" onClick={loadPage} disabled={loading}>
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
      {loading && buckets.length === 0 && <p class="xp-note">Loading…</p>}
    </ExploreSheet>
  );
}
